import { Download, FileCheck2, ShieldCheck } from "lucide-react";
import { trackBlogAssetDownload } from "@/lib/analytics";

const SCORECARD_PDF_URL = "/media/aeo-evidence-scorecard-checklist-2026_252991a7.pdf";

interface AEOScorecardLeadMagnetProps {
  postSlug: string;
}

/**
 * Contextual, direct-download lead magnet for the transparent AEO assessment article.
 * The PDF is a practical worksheet; it does not gate access or imply a result.
 */
export default function AEOScorecardLeadMagnet({ postSlug }: AEOScorecardLeadMagnetProps) {
  const handleDownload = () => {
    trackBlogAssetDownload({
      asset_name: "aeo_evidence_scorecard_checklist_2026",
      asset_url: SCORECARD_PDF_URL,
      post_slug: postSlug,
      placement: "post_introduction",
    });
  };

  return (
    <section
      className="my-8 overflow-hidden rounded-2xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 via-slate-950/5 to-amber-500/10"
      aria-labelledby="aeo-scorecard-title"
    >
      <div className="h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-amber-400" />
      <div className="p-6 sm:p-7">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 dark:text-teal-300">
              <FileCheck2 className="h-4 w-4" aria-hidden="true" />
              Free downloadable worksheet
            </div>
            <h2 id="aeo-scorecard-title" className="font-serif text-2xl font-semibold tracking-tight text-foreground">
              AEO Evidence Scorecard: the two-page readiness checklist
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Score technical eligibility, entity clarity, source discipline, semantic parity, and repeatable measurement. The worksheet is designed to turn an unexplained number into a documented worklist.
            </p>
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
              A readiness score is not a forecast or a guarantee of a ranking, citation, lead, or conversion.
            </p>
          </div>

          <a
            href={SCORECARD_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDownload}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/20 transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download the PDF
          </a>
        </div>
      </div>
    </section>
  );
}
